// Basic
import { useEffect } from 'react';
import PropTypes from 'prop-types';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
// Common
import customizationFields from '../../../../fields/customizationFields';
import isOptionChecked from '../../../../utils/isOptionChecked';
// Lang
import { nsCustomization } from '@/app/i18n/namespaces';
import CheckboxHelper from '@/common-utils/components/renders/checkbox/CheckboxHelper';

/**
 * @param root0
 * @param root0.checkedPaymentTickets
 * @param root0.setCheckedPaymentTickets
 * @param root0.checkedOptions
 * @param root0.customization
 * @param root0.isLoading
 * @param root0.isDisabled
 * @returns {JSX} PaymentTickets
 */
function PaymentTickets({
    checkedPaymentTickets = [],
    setCheckedPaymentTickets,
    checkedOptions = [],
    customization,
    isLoading = false,
    isDisabled = false
}) {

    // Constants
    const entityPrefix = 'details.functionnalities.payment_tickets';
    const merchantOptions = [
        customizationFields.MPF_OPTIONS.NATIVE_MERCHANT_RECEIPT.key
    ];
    const clientOptions = [
        customizationFields.MPF_OPTIONS.NATIVE_CUSTOMER_RECEIPT.key,
        customizationFields.MPF_OPTIONS.DEMATERIALIZED_RECEIPT_QR_CODE.key
    ];

    // Function -- Toggle option helper
    const toggleOption = (key) => {
        const isChecked = checkedOptions.some((item) => item.name === key);

        if (isChecked) {
            setCheckedPaymentTickets(checkedPaymentTickets.filter((item) => item.name !== key));
        } else {
            setCheckedPaymentTickets([...checkedPaymentTickets, { name: key, value: null }]);
        }
    };

    // Hook -- Initialize from customization if not empty
    useEffect(() => {
        if (customization && !checkedPaymentTickets.length) {
            const enabledOptions = customization[customizationFields.MPF_OPTIONS.key] || [];

            const defaults = [...merchantOptions, ...clientOptions].filter((key) =>
                enabledOptions.some((item) => item.name === key)
            );

            setCheckedPaymentTickets(defaults.map((key) => ({ name: key, value: null })));
        } else {
            // Set checked payment tickets from checked options default values
            setCheckedPaymentTickets(checkedOptions
                .filter((item) => [...merchantOptions, ...clientOptions].some((key) => key === item.name))
                .map((item) => ({ name: item.name, value: null }))
            );
        }
    }, [customization]);

    return (
        <div className='flex flex-col lg:flex-row w-full lg:gap-8 space-y-4 lg:space-y-0 p-4'>
            {isLoading && (
                <div className='flex flex-col lg:flex-row w-full gap-4 lg:gap-8'>
                    <div className='flex-1 basis-0 space-y-4'>
                        <div className='text-left'>
                            <Skeleton className='h-4 w-40 bg-gray-200' />
                        </div>
                        <div className='flex items-start gap-3'>
                            <Skeleton className='h-4 w-64 bg-gray-200' />
                        </div>
                    </div>
                    <div className='flex-1 basis-0 space-y-4'>
                        <div className='text-left'>
                            <Skeleton className='h-4 w-40 bg-gray-200 ' />
                        </div>
                        {[1, 2].map((i) => (
                            <div key={i} className='flex items-start gap-3'>
                                <Skeleton className='h-4 w-64 bg-gray-200' />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {!isLoading && (
                <>
                    <div className='flex-1 basis-0 space-y-4'>
                        <div className='text-left'>
                            <Entity
                                className='text-sm underline'
                                entity={`${entityPrefix}.merchant.title`}
                                namespace={nsCustomization}
                            />
                        </div>
                        <CheckboxHelper
                            isChecked={isOptionChecked(checkedPaymentTickets, customizationFields.MPF_OPTIONS.NATIVE_MERCHANT_RECEIPT.key)}
                            onCheckedChange={() => toggleOption(customizationFields.MPF_OPTIONS.NATIVE_MERCHANT_RECEIPT.key)}
                            isDisabled={isDisabled}
                            entity={`${entityPrefix}.merchant.nativeMerchantReceipt`}
                            namespace={nsCustomization}
                        />
                    </div>
                    <div className='flex-1 basis-0 space-y-4'>
                        <div className='text-left'>
                            <Entity
                                className='text-sm underline'
                                entity={`${entityPrefix}.client.title`}
                                namespace={nsCustomization}
                            />
                        </div>
                        {clientOptions.map((key, index) => (
                            <CheckboxHelper
                                key={key}
                                isChecked={isOptionChecked(checkedPaymentTickets, key)}
                                onCheckedChange={() => toggleOption(key)}
                                isDisabled={isDisabled}
                                entity={`${entityPrefix}.client.option_${index + 1}`}
                                namespace={nsCustomization}
                            />
                        ))}
                    </div>
                </>
            )
            }
        </div>
    );
}

PaymentTickets.propTypes = {
    checkedPaymentTickets: PropTypes.array,
    setCheckedPaymentTickets: PropTypes.func,
    checkedOptions: PropTypes.array,
    customization: PropTypes.object,
    isLoading: PropTypes.bool,
    isDisabled: PropTypes.bool
};
export default PaymentTickets;